export interface OrderItem {
  ml_order_id: number;
  item_id: string;
  seller_sku: string;
  quantity: number;
  unit_price: number;
  sale_fee: number;
  currency_id: string;
  title: string | null;
  thumbnail: string;
  gtin: string | null;
  permalink?: string | null;
}

export interface Purchase {
  id: number;
  ml_order_id: number;
  asin: string;
  amazon_status: string;
  unit_cost: number | null;
  total_cost: number | null;
  tracking_number: string | null;
  amazon_order_id: string | null;
  link_source: string | null;
  carrier: string | null;
  delivery_date: string | null;
  offer_delivery_text: string | null;
  error_message: string | null;
  rejection_code: string | null;
  stock_po_id: number | null;
  po_id: number | null;
  po_number: string | null;
  po_status: string | null;
  ml_shipping_cost: number | null;
  profit: number | null;
  profit_margin: number | null;
  delivery_issue_kind: string | null;
  delivery_issue_detail: string | null;
  delivery_issue_detected_at: string | null;
  /** Seguimiento de precio/stock en Keepa activo (null si no hay) — 2026-09-20 */
  price_watch: {
    id: number;
    status: string;
    reason: 'manual' | 'auto_price' | 'auto_delivery';
    target_price_usd: number | string;
    max_profit_price_usd: number | string | null;
    last_check_at: string | null;
    last_check_result: string | null;
    last_check_amazon_price: number | string | null;
    expires_at: string;
    created_by: string;
  } | null;
}

export interface Shipment {
  ml_shipment_id: number;
  ml_order_id: number;
  status: string;
  substatus: string | null;
  tracking_number: string | null;
  shipping_method_name: string | null;
  logistic_type: string | null;
  shipping_cost: number | null;
  estimated_delivery_date: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
}

export interface Claim {
  ml_claim_id: number;
  ml_order_id: number;
  status: string;
  type: string;
  stage: string;
  reason_id: string;
  detail_title: string | null;
  detail_problem: string | null;
  detail_due_date: string | null;
  detail_description: string | null;
  return_id: number | null;
  return_status: string | null;
  return_status_money: string | null;
  return_refund_at: string | null;
  return_shipment_status: string | null;
  return_tracking_number: string | null;
  return_destination_address: string | null;
  return_destination_city: string | null;
  return_destination_state: string | null;
  return_destination_country: string | null;
  return_destination_zip: string | null;
  // Devolución recibida en el depósito por escaneo de su guía (2026-09-23)
  return_received_at?: string | null;
  return_received_by?: string | null;
}

export interface InternalPurchase {
  id: number;
  ml_order_id: number;
  asin: string;
  internal_sku: string | null;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  status: string;              // hold | released | returned | cancelled
  stock_product_id: number;
  gross_profit: number | null;
  profit_margin: number | null;
  created_at: string;
  stock_source_type: string | null;  // mixed | internal
  cost_layer_source: string | null;  // po_received | initial_load
  cost_layer_ref: string | null;     // SPO-1-20260402-001
  supplier_name: string | null;
}

export interface UnifiedOrder {
  type: 'single' | 'pack';
  ml_order_id?: number;
  pack_id?: number | null;
  status: string;
  paid_amount: number;
  currency_id: string;
  context_site: string;
  buyer: { name: string; full_name: string | null; id: number };
  date_created: string;
  has_claim: boolean;
  fulfilled: boolean;
  items: OrderItem[];
  purchases: Purchase[];
  shipments: Shipment[];
  claims: Claim[];
  internal_purchases: InternalPurchase[];
  profit: number | null;
  margin_pct: number | null;
  net_revenue: number;
  amazon_cost: number;
  stock_cost: number;
  wh_status: 'received' | 'packed' | 'closed' | null;
  wh_batch_id: number | null;
  seller_reimbursed: boolean;
  seller_reimbursed_at: string | null;
  seller_reimbursed_source: string | null;
  seller_reimbursed_amount: number | null;
  seller_reimbursed_ref: string | null;
  returned_to_seller: boolean;
  returned_to_seller_at: string | null;
  returned_to_seller_country: string | null;
  /** Vigilancia Atrasados: ML entregó DESPUÉS de marcar reintegro/pérdida/stock */
  late_delivered_at: string | null;
  /** Caso de pérdida confirmada para un envío atrasado */
  delayed_loss_confirmed_at: string | null;
  orders?: Array<{ ml_order_id: number; status: string; paid_amount: number }>;
  unread_count: number;
  /** Reclamo de no entregadas a ML (etapa 2): expediente vigente; en un pack, el más urgente. */
  nd_claim?: {
    status: string;
    round: number;
    claimed_at: string;
    reason: string | null;
    case_id: string | null;
    note: string | null;
  } | null;
}

export interface OrdersResponse {
  success: boolean;
  data: {
    orders: UnifiedOrder[];
    pagination: { page: number; limit: number; total: number };
    summary: {
      total_orders: number;
      pending_purchases: number;
      total_revenue: number;
      total_profit: number;
      open_claims: number;
      not_delivered_count: number;
      delayed_count: number;
      delivery_issues_count: number;
      ready_to_ship_count: number;
      stale_released_count: number;
      unread_messages_count: number;
    };
  };
}

/** Días de tolerancia después de la fecha estimada de entrega (espejo de DELAYED_GRACE_DAYS backend). */
export const DELAYED_GRACE_DAYS = 3;

/**
 * Orden "atrasada": paga, envío todavía en viaje en ML y fecha estimada vencida
 * hace más de DELAYED_GRACE_DAYS. Mismo criterio que el filtro del backend
 * (las exclusiones por reintegro/pérdida/stock se aplican en cada botón).
 */
export function isDelayedShipment(order: Pick<UnifiedOrder, 'status' | 'shipments'>): boolean {
  if (order.status !== 'paid') return false;
  const cutoff = Date.now() - DELAYED_GRACE_DAYS * 86_400_000;
  return order.shipments.some(
    (s) =>
      s.status === 'shipped' &&
      !!s.estimated_delivery_date &&
      new Date(s.estimated_delivery_date).getTime() < cutoff
  );
}

export interface OrderFilters {
  status: string;
  site: string;
  search: string;
  dateFrom: string;
  dateTo: string;
  pendingOnly: boolean;
  claimsOnly: boolean;
  notDeliveredOnly: boolean;
  notDeliveredAll: boolean;
  /** Botón "Atrasados": en viaje con fecha estimada vencida, sin resolver */
  delayedOnly: boolean;
  deliveryIssuesOnly: boolean;
  readyToShipOnly: boolean;
  unreadOnly: boolean;
  staleReleasedOnly: boolean;
  whStatus: string;
  /** Filtro por subestado de envío: 'fraudulent' | 'receiver_insufficient_quota' | '' */
  shipSubstatus: string;
}

export const SITE_COLORS: Record<string, string> = {
  MLA: '#3483FA',
  MLM: '#00A650',
  MLC: '#F23D4F',
  MCO: '#FF7733',
  MLU: '#8B5CF6',
  MLB: '#FFE600',
};

export const SITE_FLAGS: Record<string, string> = {
  MLA: '🇦🇷',
  MLM: '🇲🇽',
  MLC: '🇨🇱',
  MCO: '🇨🇴',
  MLU: '🇺🇾',
  MLB: '🇧🇷',
};

export const AMZ_STATUS_COLORS: Record<string, string> = {
  ordered: 'var(--color-info)',
  shipped: 'var(--color-warning)',
  delivered: 'var(--color-success)',
  delivered_ml: 'var(--color-success)',
  pending_approval: 'var(--color-warning)',
  safeguard_hold: 'var(--color-warning)',
  // Compra HECHA en Amazon con oferta sustituida (precio/entrega distintos): espera decisión humana.
  needs_review: '#ea580c',
  error: 'var(--color-error)',
  failed: 'var(--color-error)',
  cancelled: 'var(--color-text-muted)',
};

export const ML_STATUS_COLORS: Record<string, string> = {
  paid: 'var(--color-success)',
  shipped: 'var(--color-info)',
  delivered: 'var(--color-success)',
  cancelled: 'var(--color-error)',
};

export const WH_STATUS_COLORS: Record<string, string> = {
  received: 'var(--color-warning)',
  packed: 'var(--color-info)',
  closed: 'var(--color-success)',
};

export const WH_STATUS_LABELS: Record<string, string> = {
  received: 'WH: Received',
  packed: 'WH: Packed',
  closed: 'WH: Closed',
};

export const STOCK_STATUS_COLORS: Record<string, string> = {
  hold: 'var(--color-warning)',
  released: 'var(--color-success)',
  returned: 'var(--color-info)',
  cancelled: 'var(--color-text-muted)',
};
